import React, { useEffect, useRef, useState } from "react";
import { ukDayYear as fmtDate } from "@/lib/time";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AgreementDialog } from "./AgreementDialog";
import { TermsHistory } from "./TermsHistory";
import { PartnerPaperworkCard } from "./PartnerPaperworkCard";
import { toast } from "sonner";
import { FileSignature, FileDown, ShieldCheck, Loader2, Landmark, Lock,
         PenLine, CheckCircle2, Receipt } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const StatusPill = ({ signed }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 h-6 text-[10.5px] font-semibold ${
    signed ? "border-teal-200 bg-teal-50 text-teal-700" : "border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] text-[#3B63A8]"}`}>
    {signed ? <><ShieldCheck className="size-3" /> Fully executed</> : <><PenLine className="size-3" /> Awaiting your countersignature</>}
  </span>
);

function DocCard({ doc, data, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const signed = data.status === "signed";
  const isNda = doc === "nda";

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/portal/${doc}.pdf`, { params: { v: Date.now() }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `connexis-${isNda ? "mutual-nda" : "agreement"}${signed ? "-executed" : ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed — please try again"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-2xl border p-5 transition-colors ${signed ? "border-slate-200 bg-white" : "border-[#7da9ff]/45 bg-[#7da9ff]/[0.07]"}`}
         data-testid={`doc-card-${doc}`}>
      <div className="flex items-start justify-between gap-3">
        <span className={`grid place-items-center size-10 rounded-xl shrink-0 border ${signed ? "border-teal-200 bg-teal-50 text-teal-600" : "border-[#7da9ff]/45 bg-[#7da9ff]/[0.18] text-[#3B63A8]"}`}>
          {isNda ? <Lock className="size-4.5" /> : <FileSignature className="size-4.5" />}
        </span>
        <StatusPill signed={signed} />
      </div>
      <div className="mt-3.5 text-[15px] font-semibold text-slate-900">{data.title}{data.version > 1 ? ` · v${data.version}` : ""}</div>
      <p className="mt-1 text-[12.5px] text-slate-500 leading-relaxed">
        {isNda
          ? "Mutual confidentiality over applicant financials, lending criteria, panel terms and deal flow. Runs 3 years from last disclosure."
          : data.live_terms?.short || data.terms?.short
            ? `${data.live_terms?.short || data.terms?.short}. ${data.live_terms?.schedule || data.terms?.schedule}.`
            : "Your commercial terms with Connexis."}
      </p>
      <div className="mt-2.5 text-[11.5px] text-slate-400">
        {signed
          ? `Countersigned by ${data.signed_name || "you"} on ${fmtDate(data.signed_at)}${data.method === "wet_ink" ? " (wet ink, scan on file)" : ""}`
          : data.sent_at ? `Issued ${fmtDate(data.sent_at)} · Connexis has signed` : "Not yet issued — the team will send it shortly"}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {!signed && data.status !== "not_sent" && (
          <Button onClick={() => setOpen(true)} className="rounded-full bg-[#0B1220] hover:bg-[#1a2436] h-9 text-[12.5px] font-semibold"
                  data-testid={`doc-countersign-${doc}`}>
            <PenLine className="size-3.5 mr-1.5" /> Read &amp; countersign
          </Button>
        )}
        <Button variant="outline" onClick={download} disabled={busy || data.status === "not_sent"} className="rounded-full h-9 text-[12.5px]"
                data-testid={`doc-download-${doc}`}>
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <><FileDown className="size-3.5 mr-1.5" /> Download PDF</>}
        </Button>
        {signed && (
          <Button variant="ghost" onClick={() => setOpen(true)} className="rounded-full h-9 text-[12.5px] text-slate-500"
                  data-testid={`doc-view-${doc}`}>
            View details
          </Button>
        )}
      </div>
      <AgreementDialog open={open} onOpenChange={setOpen} data={data} doc={doc} onSigned={onChanged} />
    </div>
  );
}

function PayoutSettings({ me, onSaved }) {
  const [form, setForm] = useState({ bank_account_name: "", bank_sort_code: "", bank_account_no: "", vat_number: "" });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const box = useRef(null);

  // Deep link from Connie and the tidy-up: /introducer/account#payouts lands right on this box.
  useEffect(() => {
    if (window.location.hash !== "#payouts") return undefined;
    const t = setTimeout(() => box.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setForm({
      bank_account_name: me?.bank_account_name || "",
      bank_sort_code: me?.bank_sort_code || "",
      bank_account_no: me?.bank_account_no || "",
      vat_number: me?.vat_number || "",
    });
    setEditing(!me?.payout_ready);
  }, [me]);

  const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const sortOk = form.bank_sort_code.replace(/\D/g, "").length === 6;
  const accOk = form.bank_account_no.replace(/\D/g, "").length === 8;
  const canSave = form.bank_account_name.trim().length > 1 && sortOk && accOk;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/refer/payout-details", {
        bank_account_name: form.bank_account_name.trim(),
        bank_sort_code: form.bank_sort_code,
        bank_account_no: form.bank_account_no.replace(/\D/g, ""),
        vat_number: form.vat_number.trim() || null,
      });
      toast.success("Payout details saved — they'll appear on your next statement");
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save your payout details"); }
    finally { setSaving(false); }
  };

  return (
    <div ref={box} id="payouts" className="scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-5" data-testid="payout-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center size-10 rounded-xl border border-slate-200 bg-slate-50 text-slate-600">
            <Landmark className="size-4.5" />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">Where we send your commission</div>
            <div className="text-[12px] text-slate-500">Printed on every statement — BACS payouts {me?.payout_frequency || "monthly"}.</div>
          </div>
        </div>
        {me?.payout_ready ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2.5 h-6 text-[10.5px] font-semibold text-teal-700" data-testid="payout-ready-pill">
            <CheckCircle2 className="size-3" /> Ready for payout
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] px-2.5 h-6 text-[10.5px] font-semibold text-[#3B63A8]" data-testid="payout-missing-pill">
            Bank details needed
          </span>
        )}
      </div>

      {!editing && me?.payout_ready ? (
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-[13px]">
          <div><div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Account name</div><div className="mt-1 font-medium text-slate-900">{me.bank_account_name}</div></div>
          <div><div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Sort code</div><div className="mt-1 font-mono tabular-nums text-slate-900">{me.bank_sort_code}</div></div>
          <div><div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Account</div><div className="mt-1 font-mono tabular-nums text-slate-900">{me.bank_account_masked}</div></div>
          {me.vat_number && <div><div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">VAT</div><div className="mt-1 font-mono text-slate-900">{me.vat_number}</div></div>}
          <div className="sm:col-span-3">
            <Button variant="outline" onClick={() => setEditing(true)} className="rounded-full h-9 text-[12.5px]" data-testid="payout-edit">Update details</Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600 mb-1.5">Account name *</div>
            <Input value={form.bank_account_name} onChange={(e) => setF("bank_account_name")(e.target.value)}
                   placeholder="As it appears on your bank account" data-testid="payout-account-name" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600 mb-1.5">Sort code *</div>
            <Input value={form.bank_sort_code} inputMode="numeric" placeholder="04-00-04"
                   onChange={(e) => {
                     const d = e.target.value.replace(/\D/g, "").slice(0, 6);
                     setF("bank_sort_code")(d.replace(/(\d{2})(?=\d)/g, "$1-"));
                   }}
                   className="font-mono tabular-nums" data-testid="payout-sort-code" />
            {form.bank_sort_code && !sortOk && <div className="mt-1 text-[10.5px] text-[#3B63A8]">Six digits, e.g. 04-00-04</div>}
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600 mb-1.5">Account number *</div>
            <Input value={form.bank_account_no} inputMode="numeric" placeholder="12345678"
                   onChange={(e) => setF("bank_account_no")(e.target.value.replace(/\D/g, "").slice(0, 8))}
                   className="font-mono tabular-nums" data-testid="payout-account-no" />
            {form.bank_account_no && !accOk && <div className="mt-1 text-[10.5px] text-[#3B63A8]">Eight digits</div>}
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600 mb-1.5">VAT number (optional)</div>
            <Input value={form.vat_number} onChange={(e) => setF("vat_number")(e.target.value.toUpperCase())}
                   placeholder="GB123456789" className="font-mono" data-testid="payout-vat" />
          </div>
          <div className="sm:col-span-2 flex items-center gap-2 pt-1">
            <Button onClick={save} disabled={!canSave || saving} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] h-10 px-5 font-semibold" data-testid="payout-save">
              {saving ? <Loader2 className="size-4 animate-spin" /> : "Save payout details"}
            </Button>
            {me?.payout_ready && <Button variant="ghost" onClick={() => setEditing(false)} className="h-10 text-slate-500" data-testid="payout-cancel">Cancel</Button>}
            <span className="text-[10.5px] text-slate-400 ml-1">Held encrypted at rest · only used for your payouts</span>
          </div>
        </div>
      )}
    </div>
  );
}

function StatementArchive() {
  const [busy, setBusy] = useState(null);
  const now = new Date();
  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
  });

  const grab = async ({ year, month }) => {
    setBusy(`${year}-${month}`);
    try {
      const r = await api.get(`/refer/statement/${year}/${month}.pdf`, { params: { v: Date.now() }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `connexis-commission-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`${MONTHS[month - 1]} ${year} statement downloaded`);
    } catch { toast.error("That statement could not be generated — try another month"); }
    finally { setBusy(null); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="statement-archive">
      <div className="flex items-center gap-2.5">
        <span className="grid place-items-center size-10 rounded-xl border border-slate-200 bg-slate-50 text-slate-600"><Receipt className="size-4.5" /></span>
        <div>
          <div className="text-[15px] font-semibold text-slate-900">Statement archive</div>
          <div className="text-[12px] text-slate-500">Every month on Connexis letterhead — deal lines, VAT and your bank details.</div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {months.map((m) => (
          <Button key={`${m.year}-${m.month}`} variant="outline" onClick={() => grab(m)} disabled={busy === `${m.year}-${m.month}`}
                  className="rounded-full h-9 text-[12.5px]" data-testid={`statement-${m.year}-${m.month}`}>
            {busy === `${m.year}-${m.month}` ? <Loader2 className="size-3.5 animate-spin" /> : <><FileDown className="size-3.5 mr-1.5" /> {MONTHS[m.month - 1].slice(0, 3)} {m.year}</>}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** DocumentsPanel — one home for agreements, the NDA, payout details and statements. */
export function DocumentsPanel({ me, onChanged }) {
  const [docs, setDocs] = useState(null);

  const load = async () => {
    try { const r = await api.get("/portal/documents"); setDocs(r.data); } catch { setDocs(null); }
  };
  useEffect(() => { load(); }, []);
  if (!docs) return null;

  const isIntroducer = docs.kind === "referrer";
  const refresh = () => { load(); onChanged?.(); };

  return (
    <div className="space-y-4" data-testid="documents-panel">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-900">Documents &amp; payouts</h2>
          <p className="mt-0.5 text-[12.5px] text-slate-500">
            Pre-signed by {docs.connexis_signatory} — countersign online, keep every copy here.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3.5">
        <DocCard doc="agreement" data={docs.agreement} onChanged={refresh} />
        <DocCard doc="nda" data={docs.nda} onChanged={refresh} />
      </div>

      <TermsHistory doc="agreement" reloadKey={`${docs.agreement?.version || 1}-${docs.agreement?.status || ""}`}
                    historyPath="/portal/documents/history"
                    versionPath={(v) => `/portal/documents/history/${v}.pdf`} />

      {isIntroducer && <PayoutSettings me={me} onSaved={refresh} />}
      {isIntroducer && <StatementArchive />}
      <PartnerPaperworkCard />
    </div>
  );
}
